import retry from 'async-retry';
import { isObject } from 'es-toolkit/compat';
import { appError } from '../actions/app';
import { assessAuth } from '../actions/auth';
import isServer from '../utils/isServer';

function enhanceResponse(data) {
  const finalData = !data || isObject(data) ? { ...data } : { data };

  return {
    ...finalData,
    loadedTime: isServer() ? null : Date.now(),
  };
}

export default function apiClientEffectsMiddleware(apiClient, errorCallback, logEndpoint) {
  return (store) => (next) => (action) => {
    const { type, meta, api } = action;
    let finalAction = action;

    if (api) {
      const payload = new Promise(async (resolve, reject) => {
        const { endpoint, args, transform, registerAppError = true, retries = 0 } = api;

        // Log the endpoint value if logEndpoint is provided
        if (logEndpoint && endpoint) {
          logEndpoint(endpoint);
        }

        const handleAuth = assessAuth(store.dispatch);

        const handleAuthError = (error) => {
          throw handleAuth(error);
        };

        const registerApiError = (err) => {
          // Effects that define `registerAppError=false` will effectively prevent 404/500
          // errors/pages from showing based on the specific api failure response.
          if (registerAppError) {
            store.dispatch(appError(err));
          }

          errorCallback(err, type);

          throw err;
        };

        let apiClientFn;
        const [serviceName, method] = endpoint;

        let service = apiClient[serviceName];

        // Some WAC-beta service getters are functions that lazy-load the service instance
        if (typeof service === 'function') {
          service = await service();
        }

        apiClientFn = service[method].bind(service);

        if (!apiClientFn) {
          reject(new Error(`<apiClient>.${endpoint.join('.')} is not a valid feature`));
        } else {
          const resp = retry(() => apiClientFn(...(args || [])), { retries })
            .catch(registerApiError)
            .then(handleAuth, handleAuthError)
            .then((data) => {
              const transformedData = transform ? transform(data) : data;

              return endpoint[0] === 'ui' ? { content: transformedData } : transformedData;
            })
            .then(enhanceResponse);

          resolve(resp);
        }
      });

      finalAction = {
        type,
        meta,
        payload,
      };
    }

    return next(finalAction);
  };
}
